import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ViewportScroller, NgTemplateOutlet, SlicePipe, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, HostBinding, Input,
  input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation
} from '@angular/core';

import {
  DeviceService,
  IconSizeEnum,
  ImageRatioEnum,
  IMedia,
  IMediaGallery,
  ISdFormComponentItem,
  ISkeeper,
  ListSpacing,
  PageTypeEnum,
  ReinsurancesPdpType,
  SdBannerComponent,
  SdButtonComponent,
  SdButtonIconComponent,
  SdFormSelectComponent,
  SdHtmlComponent,
  SdIconComponent,
  SdLibConfig,
  SdListComponent,
  SdProductPromoComponent,
  SdReinsurancesPdpComponent,
  SdSkeletonComponent,
  SdTagComponent,
  SdThemeComponent,
  SdTitleComponent,
  SectionTypeEnum,
  SkeletonAppearanceEnum,
  TypographyUtils
} from '@seb/web-design-system/core';

import { SdSectionComponent } from '@seb/web-design-system/core/src/lib/components/section/section.component';
import { SdGalleryComponent } from '@seb/web-design-system/sliders';
import { SdProductSellerComponent } from '@seb/web-design-system/product-seller';
import { IKlarna, SdProductInfoComponent } from '@seb/web-design-system/product-info';
import { SdProductBundleComponent } from '@seb/web-design-system/product-bundle';
import { SdProductFeaturesComponent } from '@seb/web-design-system/product-features';
import { SdProductLinkCardComponent } from '@seb/web-design-system/product-link-card';
import { SdDividedPaymentComponent } from '@seb/web-design-system/payment';

import type { IOverview } from './overview.interface';

@Component({
  selector: 'sd-product-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgClass,
    NgTemplateOutlet,
    SdGalleryComponent,
    SdProductInfoComponent,
    SdButtonComponent,
    SdProductPromoComponent,
    SdButtonIconComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormSelectComponent,
    SdReinsurancesPdpComponent,
    SdProductBundleComponent,
    SdTitleComponent,
    SdSkeletonComponent,
    SdListComponent,
    SdTagComponent,
    SdBannerComponent,
    SdIconComponent,
    SdProductSellerComponent,
    SlicePipe,
    TranslateModule,
    SdProductFeaturesComponent,
    SdProductLinkCardComponent,
    SdHtmlComponent,
    SdSectionComponent,
    SdDividedPaymentComponent
  ]
})
export class SdProductOverviewComponent extends SdThemeComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  reviewsClickable = true;

  @Input()
  isLoading = false;

  @Input()
  overview?: IOverview;

  @Input()
  showSkeeper = false;

  @Input()
  objSkeeper: ISkeeper;

  @Input()
  images: IMediaGallery[];

  @Input()
  quantityDisabled = false;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel: number;

  @Input()
  isBundle: boolean;

  @Input()
  isForfait: boolean;

  @Input()
  klarnaData?: IKlarna;

  @Input()
  madeInData: IMedia;

  @Input()
  isOpenRef: boolean;

  @Input()
  pageTemplate: string;

  @Input()
  isSSR = true;

  @Input()
  refurbishedData: IMedia;
  iAdvizeEnabled = input<boolean>(false);

  @Output()
  favorite = new EventEmitter<boolean>();

  @Output()
  cart = new EventEmitter<number>();

  @Output()
  changeStore = new EventEmitter<void>();

  @Output()
  notify = new EventEmitter<boolean>();

  @Output()
  compatibilityCheck = new EventEmitter<void>();

  @Output()
  repairabilityInfoClick = new EventEmitter<void>();

  @Output()
  repairabilityLinkClick = new EventEmitter<void>();

  @Output()
  productSellerModal = new EventEmitter<boolean>();

  @Output()
  dividedClick: EventEmitter<void> = new EventEmitter();

  @Output()
  productFeatureClick: EventEmitter<void> = new EventEmitter();

  @Output()
  reviewsClicked = new EventEmitter<number>();

  @Output()
  openRecommendedPriceModal = new EventEmitter<void>();

  @Output()
  reinsurancesPdpClick = new EventEmitter<ReinsurancesPdpType>();

  @Input() class = '';

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class
    ]
      .filter(Boolean)
      .join(' ');
  }

  imageHasBorder!: boolean;
  imageRatio!: ImageRatioEnum;
  showWishList!: boolean;

  skeletonAppearanceEnum = SkeletonAppearanceEnum;
  listSpacing = ListSpacing;
  isOpen = false;
  isOpenDesc = false;
  iconSize = IconSizeEnum;
  overviewForm: UntypedFormGroup;
  subscriptions = new Subscription();
  isMobile: boolean;
  limit = 500;
  itemQty: ISdFormComponentItem[];
  getHTMLText = TypographyUtils.getHTMLText;
  pageTypeEnum = PageTypeEnum;
  numberOfRefSavToDisplay = 2;
  currentNumberOfRefSavToDisplay = 2;
  _isOpenRef = false;
  SectionTypeEnum = SectionTypeEnum;

  constructor(
    private cdRef: ChangeDetectorRef,
    private sdLibConfig: SdLibConfig,
    private deviceService: DeviceService,
    private formBuilder: UntypedFormBuilder,
    private viewportScroller: ViewportScroller
  ) {
    super();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile = x;
        this.cdRef.markForCheck();
      }),
    );

    this.itemQty = Array(this.overview?.maxQty || 6)
      .fill('')
      .map((x, i) => {
        return {
          value: i + 1
        };
      });

    this.overviewForm = this.formBuilder.group({
      quantitySelector: { value: 1 }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    if (changes['brand'] && (changes['brand'].previousValue !== changes['brand'].currentValue)) {
      const { productImageHasBorder, productImageRatio, wishList } = this.sdLibConfig.get(this.brand);

      this.imageHasBorder = productImageHasBorder;
      this.imageRatio = productImageRatio;
      this.showWishList = wishList;
    }

    if (this.isOpenRef) {
      this.openReadMore();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  addToFavorite() {
    if (this.overview) {
      this.overview.isInFavorite = !this.overview.isInFavorite;
      this.favorite.emit(this.overview.isInFavorite);
    }
  }

  addToCart() {
    if (this.overviewForm) {
      this.cart.emit(this.overviewForm.get('quantitySelector').value.value);
    }
  }

  onDividedClick() {
    this.dividedClick.emit();
  }

  onProductFeatureClick() {
    this.productFeatureClick.emit();
  }

  onChangeStore() {
    return this.changeStore.emit();
  }

  stockNotify() {
    return this.notify.emit(true);
  }

  selectLink(url: string, event: Event) {
    event.preventDefault();
    this.compatibilityCheck.emit();
    this.viewportScroller.scrollToAnchor(url);
  }

  toggleProductSellerModal(event) {
    this.productSellerModal.emit(event);
  }

  onRepairabilityInfoClick() {
    this.repairabilityInfoClick.emit();
  }

  onRepairabilityLinkClick() {
    this.repairabilityLinkClick.emit();
  }

  onOpenRecommendedPriceModal() {
    this.openRecommendedPriceModal.emit();
  }

  scrollTo(url: string, event: Event) {
    event.preventDefault();
    this.viewportScroller.scrollToAnchor(url);
  }

  onReinsurancesPdpClick(type: ReinsurancesPdpType) {
    this.reinsurancesPdpClick.emit(type);
  }

  openReadMore() {
    this._isOpenRef = !this._isOpenRef;
    this.currentNumberOfRefSavToDisplay = this._isOpenRef
      ? this.overview?.refSAV.length
      : this.numberOfRefSavToDisplay;
  }
}
